import classnames from 'classnames'
import { useRef } from 'react'

import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import RichText from '@src/quince-site-common/src/domains/RichText'

import { RichText as RichTextC } from '../../molecules'
import useLockBodyScroll from '../modal/hooks/useLockBodyScroll'

import styles from './airlineCompatibility.module.scss'
import { getTransformedACDataForUI } from './AirlineCompatibility.utils'
import Check from './icons/check.inline.svg'
import Cross from './icons/cross.inline.svg'
import Incompatible from './icons/incompatible.inline.svg'

export interface AirlineCompatibilityProps {
  className?: string
  show: boolean
  onCloseAirlineCompatbility: () => void
  airlineCompatibilityListingCS?: CustomSection[] | undefined
}

const AirlineCompatibility = (props: AirlineCompatibilityProps) => {
  const {
    show = false,
    airlineCompatibilityListingCS = null,
    className = '',
    onCloseAirlineCompatbility = EMPTY_ARROW_FUNC,
  } = props
  const intersectRef = useRef(null)
  useLockBodyScroll(show)

  if (
    !show ||
    !airlineCompatibilityListingCS ||
    (airlineCompatibilityListingCS && airlineCompatibilityListingCS.length === 0)
  )
    return null

  const acListings = getTransformedACDataForUI(airlineCompatibilityListingCS[0]?.airlineCompatibilityListing)

  return (
    <>
      <div onClick={onCloseAirlineCompatbility} className={styles.blurModal} />
      <div className={classnames(styles.wrapper, className)}>
        <Cross aria-label="close-button" className={styles.crossIcon} onClick={onCloseAirlineCompatbility} />
        <div className={styles.container}>
          <div className={styles.container__header}>{airlineCompatibilityListingCS[0]?.displayTitle}</div>

          <div className={styles.container__content}>
            {airlineCompatibilityListingCS[0]?.richTexts?.length && airlineCompatibilityListingCS[0]?.richTexts[0] && (
              <RichTextC {...RichText.makeRichTextProps(airlineCompatibilityListingCS[0].richTexts[0])} />
            )}
          </div>

          <div ref={intersectRef}></div>

          <table className={styles.container__body}>
            <thead>
              <tr>
                <th
                  className={classnames(
                    styles.container__body__tableHeader,
                    styles.container__body__tableHeader__airline,
                  )}
                >
                  Airline
                </th>
                {acListings.listings.map(listing => (
                  <th className={styles.container__body__tableHeader} key={listing.title}>
                    {listing.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={classnames(styles.container__body__row)}>
              {acListings.airlines.map(airline => {
                return (
                  <>
                    <tr>
                      <td className={styles.container__body__row__key}>{airline}</td>
                      {acListings.listings.map((listing, i) => {
                        return (
                          <td
                            className={classnames(styles.container__body__row__value, i % 2 === 0 && styles.bgColor)}
                            key={`${listing.title}__${listing.compatibility[airline]}__${i}`}
                          >
                            {listing.compatibility[airline] !== undefined &&
                              (listing.compatibility[airline] ? (
                                <Check data-testid="airlineCompatibilityCheckIcon" />
                              ) : (
                                <Incompatible data-testid="airlineCompatibilityIncompatibleIcon" />
                              ))}

                            {listing.compatibility[airline] === undefined && (
                              <span data-testid="airlineCompatibilityNA">N/A</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  </>
                )
              })}
            </tbody>
          </table>

          <div className={styles.container__footer}>
            {airlineCompatibilityListingCS[0]?.richTexts?.length && airlineCompatibilityListingCS[0].richTexts[1] && (
              <RichTextC {...RichText.makeRichTextProps(airlineCompatibilityListingCS[0].richTexts[1])} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AirlineCompatibility
